import { SUCCESS_CODE } from "./constants"
import { HttpError } from "./httpError"
import {
  ErrorInterceptor,
  HTTPProtocol,
  HttpRequestConfig,
  NextFetchRequestConfig,
  RequestInterceptor,
  ResponseInterceptor,
} from "./types"
import {
  addQueryParams,
  formatBytes,
  getClientToken,
  getLanguage,
  getRequestBodySize,
  getResponseSize,
  isClient,
  isNextRedirectError,
  isServer,
  joinURL,
  logHttpRequest,
  showErrorMessage,
} from "./utils"

class Http {
  private defaultConfig: HttpRequestConfig = {
    headers: {},
    cookieName: "SMM_auth_token",
    storageKey: "token",
    showError: false,
    // 默认启用日志（使用异步日志优化性能，不影响首屏渲染）
    // 开发环境默认启用基础日志，便于调试
    // 生产环境也默认启用，但使用异步日志避免阻塞
    log: {
      enable: true,
      level: "basic",
    },
  }

  private requestInterceptors: RequestInterceptor[] = []
  private responseInterceptors: ResponseInterceptor[] = []
  private errorInterceptors: ErrorInterceptor[] = []

  setDefaultConfig(config: HttpRequestConfig) {
    this.defaultConfig = { ...this.defaultConfig, ...config }
  }

  getDefaultConfig() {
    return this.defaultConfig
  }

  addRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor)
    return this
  }

  addResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor)
    return this
  }

  addErrorInterceptor(interceptor: ErrorInterceptor) {
    this.errorInterceptors.push(interceptor)
    return this
  }

  clearInterceptors() {
    this.requestInterceptors = []
    this.responseInterceptors = []
    this.errorInterceptors = []
    return this
  }

  private async getAuthToken(config: HttpRequestConfig): Promise<string | null> {
    let token = ""
    if (config.getToken) {
      token = (await config.getToken()) || ""
    }
    if (!token) {
      token =
        getClientToken({
          cookieName: config.cookieName,
          storageKey: config.storageKey,
        }) || ""
    }
    return token
  }

  private async request<P, T>(
    url: string,
    config: HttpRequestConfig<P> & { showOriginResponse: true }
  ): Promise<HTTPProtocol<T> | null>

  private async request<P, T>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse?: false }
  ): Promise<T | null>

  private async request<P, T>(
    url: string,
    config: HttpRequestConfig<P> & { showOriginResponse?: boolean } = {}
  ): Promise<T | HTTPProtocol<T> | null> {
    const startTime = Date.now()

    let mergedConfig = { ...this.defaultConfig, ...config }

    // 检查是否启用日志（仅在服务端开发环境输出日志）
    // 使用多重检查确保不会在客户端执行日志逻辑
    const logConfig = mergedConfig.log || this.defaultConfig.log
    const shouldLog =
      isServer && typeof window === "undefined" && logConfig?.enable !== false && logConfig?.level !== "none"
    const logLevel = logConfig?.level || "basic"
    const logFilter = logConfig?.filter

    // 如果配置了过滤函数，检查是否应该记录日志
    const shouldLogThisRequest = shouldLog && (!logFilter || logFilter(mergedConfig))

    for (const interceptor of this.requestInterceptors) {
      mergedConfig = await interceptor(url, mergedConfig)
    }

    const {
      baseURL = process.env.NEXT_PUBLIC_API_HOST_EN || "",
      timeout = 30000,
      baseURLServer = "",
      next,
      showError = false,
      getLang,
      params,
      data,
      languageQueryName = "language",
      successCode = 0,
      ...fetchOptions
    } = mergedConfig

    let language = mergedConfig.lang || ""
    if (getLang) {
      language = (await getLang()) || ""
    }
    if (!language) {
      language = getLanguage(mergedConfig) || ""
    }

    let processedUrl = url
    if (params && typeof params === "object") {
      const qp: Record<string, string> = {}
      Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        if (Array.isArray(value)) {
          value.forEach((v) => {
            qp[key] = String(v)
            processedUrl = addQueryParams(processedUrl, { [key]: qp[key] })
          })
          return
        }
        qp[key] = String(value)
      })
      const nonArrayParams: Record<string, string> = {}
      Object.entries(qp).forEach(([k, v]) => {
        nonArrayParams[k] = v
      })
      if (Object.keys(nonArrayParams).length) {
        processedUrl = addQueryParams(processedUrl, nonArrayParams)
      }
    }

    const token = await this.getAuthToken(mergedConfig)
    if (token) {
      processedUrl = addQueryParams(processedUrl, { [mergedConfig.urlTokenName || "token"]: token })
    }
    if (language) {
      processedUrl = addQueryParams(processedUrl, { [languageQueryName]: language })
    }

    const baiscUrl = (isServer ? baseURLServer : baseURL) || baseURL
    const fullUrl = baiscUrl ? joinURL(baiscUrl, processedUrl) : processedUrl

    const headers = new Headers(fetchOptions.headers)

    // 在服务端自动添加客户端 IP 到请求头
    if (isServer && mergedConfig.getClientIP) {
      const clientIP = await mergedConfig.getClientIP()
      if (clientIP) {
        headers.set("x-forwarded-for", clientIP)
      }
    }

    let body: BodyInit | null = fetchOptions?.body || null
    const method = (fetchOptions.method || "GET").toUpperCase()
    if (data !== undefined && data !== null) {
      if (data instanceof FormData) {
        body = data
        headers.delete("Content-Type")
      } else {
        const ct = headers.get("Content-Type") || ""
        if (ct.includes("application/x-www-form-urlencoded")) {
          const usp = new URLSearchParams()
          Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
            if (value === undefined || value === null) return
            if (Array.isArray(value)) {
              value.forEach((v) => usp.append(key, String(v)))
            } else {
              usp.append(key, String(value))
            }
          })
          body = usp
          if (!ct.includes("charset")) {
            headers.set("Content-Type", "application/x-www-form-urlencoded; charset=UTF-8")
          }
        } else {
          body = JSON.stringify(data)
          if (!ct) {
            headers.set("Content-Type", "application/json")
          }
        }
      }
    } else if (body instanceof FormData) {
      headers.delete("Content-Type")
    } else if ((method === "GET" || method === "HEAD") && !body) {
      headers.delete("Content-Type")
    }

    const finalOptions: RequestInit & { next?: NextFetchRequestConfig } = {
      ...fetchOptions,
      headers,
      body,
    }

    if (next) {
      finalOptions.next = next
    }

    // 计算请求体大小（用于日志）
    const requestBodySize = shouldLogThisRequest ? getRequestBodySize(body) : 0

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)

      const response = await fetch(fullUrl, {
        ...finalOptions,
        signal: controller.signal,
      })

      if (mergedConfig.getHttpOriginResponse) {
        mergedConfig.getHttpOriginResponse(response)
      }

      clearTimeout(timeoutId)

      // 获取响应体大小（用于详细日志）
      let responseBodySize: number | undefined
      if (shouldLogThisRequest && logLevel === "detailed") {
        try {
          responseBodySize = await getResponseSize(response)
        } catch {
          // 忽略获取响应大小的错误
        }
      }

      if (!response.ok) {
        const result: HTTPProtocol<T> = {
          code: -1,
          msg: `HTTP ${response.status}: ${response.statusText} url: ${url}`,
          data: null,
        }
        const error = new HttpError(
          response.status,
          result.code,
          result.msg || `HTTP ${response.status}: ${response.statusText}`,
          result.data
        )

        let errorHandled = false
        for (const interceptor of this.errorInterceptors) {
          const handled = await interceptor(error)
          if (handled === true) {
            errorHandled = true
          }
        }
        if (isClient && showError) {
          await showErrorMessage(error.getUserMessage())
        }

        // 响应处理完成，记录结束时间
        const endTime = Date.now()

        // 记录错误日志（仅在服务端）
        if (shouldLogThisRequest && isServer) {
          logHttpRequest(
            {
              timestamp: new Date(startTime).toISOString(),
              startTime,
              endTime,
              duration: endTime - startTime,
              method: finalOptions.method || "GET",
              url: fullUrl,
              statusCode: response.status,
              businessCode: result.code,
              requestSize: requestBodySize,
              requestSizeFormatted: requestBodySize > 0 ? formatBytes(requestBodySize) : undefined,
              responseSize: responseBodySize,
              responseSizeFormatted: responseBodySize ? formatBytes(responseBodySize) : undefined,
              success: false,
              error: error.message,
              errorType: "HTTP_ERROR",
            },
            logLevel
          )
        }

        if (!errorHandled) {
          throw error
        }
        return config.showOriginResponse ? result : null
      }

      const result: HTTPProtocol<T> = await response.json()

      // 如果之前没有获取响应大小，现在尝试获取（从 JSON 结果估算）
      if (shouldLogThisRequest && logLevel === "detailed" && responseBodySize === undefined) {
        try {
          responseBodySize = new Blob([JSON.stringify(result)]).size
        } catch {
          // 忽略错误
        }
      }

      if (
        (!Array.isArray(successCode) && result.code !== (successCode || SUCCESS_CODE)) ||
        (Array.isArray(successCode) && !successCode.includes(result.code))
      ) {
        const error = new HttpError(response.status, result.code, result.msg || "Request failed", result.data)

        let errorHandled = false
        for (const interceptor of this.errorInterceptors) {
          const handled = await interceptor(error)
          if (handled === true) {
            errorHandled = true
          }
        }

        if (isClient && showError) {
          await showErrorMessage(error.getUserMessage())
        }

        // 响应处理完成，记录结束时间
        const endTime = Date.now()

        // 记录业务错误日志（仅在服务端）
        if (shouldLogThisRequest && isServer) {
          logHttpRequest(
            {
              timestamp: new Date(startTime).toISOString(),
              startTime,
              endTime,
              duration: endTime - startTime,
              method: finalOptions.method || "GET",
              url: fullUrl,
              statusCode: response.status,
              businessCode: result.code,
              requestSize: requestBodySize,
              requestSizeFormatted: requestBodySize > 0 ? formatBytes(requestBodySize) : undefined,
              responseSize: responseBodySize,
              responseSizeFormatted: responseBodySize ? formatBytes(responseBodySize) : undefined,
              success: false,
              error: error.message,
              errorType: "BUSINESS_ERROR",
            },
            logLevel
          )
        }

        if (!errorHandled) {
          throw error
        }
        return config.showOriginResponse ? result : null
      }

      let responseData = result.data
      for (const interceptor of this.responseInterceptors) {
        responseData = await interceptor(response, result)
      }

      // 响应处理完成，记录结束时间
      const endTime = Date.now()

      // 记录成功日志（仅在服务端）
      if (shouldLogThisRequest && isServer) {
        logHttpRequest(
          {
            timestamp: new Date(startTime).toISOString(),
            startTime,
            endTime,
            duration: endTime - startTime,
            method: finalOptions.method || "GET",
            url: fullUrl,
            statusCode: response.status,
            businessCode: result.code,
            requestSize: requestBodySize,
            requestSizeFormatted: requestBodySize > 0 ? formatBytes(requestBodySize) : undefined,
            responseSize: responseBodySize,
            responseSizeFormatted: responseBodySize ? formatBytes(responseBodySize) : undefined,
            success: true,
          },
          logLevel
        )
      }

      return config.showOriginResponse ? result : responseData
    } catch (error) {
      if (isNextRedirectError(error)) {
        throw error
      }
      if (mergedConfig.softFailOnServer && isServer) {
        return config.showOriginResponse
          ? {
              code: -1,
              data: null,
              msg: error instanceof Error ? error.message : "请求失败",
            }
          : null
      }

      if (error instanceof HttpError) {
        throw error
      }
      if (error instanceof Error && error.name === "AbortError") {
        const timeoutError = new HttpError(408, -1, "request timeout, please check your network connection")

        let errorHandled = false
        for (const interceptor of this.errorInterceptors) {
          const handled = await interceptor(timeoutError)
          if (handled === true) {
            errorHandled = true
          }
        }
        if (isClient && showError) {
          await showErrorMessage(timeoutError.getUserMessage())
        }

        // 错误处理完成，记录结束时间
        const endTime = Date.now()

        // 记录超时错误日志（仅在服务端）
        if (shouldLogThisRequest && isServer) {
          logHttpRequest(
            {
              timestamp: new Date(startTime).toISOString(),
              startTime,
              endTime,
              duration: endTime - startTime,
              method: finalOptions.method || "GET",
              url: fullUrl,
              statusCode: 408,
              requestSize: requestBodySize,
              requestSizeFormatted: requestBodySize > 0 ? formatBytes(requestBodySize) : undefined,
              success: false,
              error: timeoutError.message,
              errorType: "TIMEOUT_ERROR",
            },
            logLevel
          )
        }

        if (!errorHandled) {
          throw timeoutError
        }
        const fallbackProto: HTTPProtocol<T> = { code: -1, data: null, msg: timeoutError.message }
        return config.showOriginResponse ? fallbackProto : null
      }

      const networkError = new HttpError(0, -1, error instanceof Error ? error.message : "network error")

      let errorHandled = false
      for (const interceptor of this.errorInterceptors) {
        const handled = await interceptor(networkError)
        if (handled === true) {
          errorHandled = true
        }
      }
      if (isClient && showError) {
        await showErrorMessage(networkError.getUserMessage())
      }

      // 错误处理完成，记录结束时间
      const endTime = Date.now()

      // 记录网络错误日志（仅在服务端）
      if (shouldLogThisRequest && isServer) {
        logHttpRequest(
          {
            timestamp: new Date(startTime).toISOString(),
            startTime,
            endTime,
            duration: endTime - startTime,
            method: finalOptions.method || "GET",
            url: fullUrl,
            requestSize: requestBodySize,
            requestSizeFormatted: requestBodySize > 0 ? formatBytes(requestBodySize) : undefined,
            success: false,
            error: networkError.message,
            errorType: "NETWORK_ERROR",
          },
          logLevel
        )
      }

      if (!errorHandled) {
        throw networkError
      }
      const fallbackProto: HTTPProtocol<T> = {
        code: -1,
        data: null,
        msg: networkError.message,
      }
      return config.showOriginResponse ? fallbackProto : null
    }
  }

  async get<P = null, T = null>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse?: false }
  ): Promise<T | null>
  async get<P = null, T = null>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse: true }
  ): Promise<HTTPProtocol<T> | null>
  async get<P = null, T = null>(
    url: string,
    config: HttpRequestConfig<P> & { showOriginResponse?: boolean } = {}
  ): Promise<T | HTTPProtocol<T> | null> {
    if (config && config.showOriginResponse === true) {
      return this.request<P, T>(url, {
        ...(config as HttpRequestConfig<P>),
        method: "GET",
        showOriginResponse: true,
      })
    } else {
      return this.request<P, T>(url, {
        ...(config as HttpRequestConfig<P>),
        method: "GET",
      })
    }
  }

  async post<P = null, T = null>(
    url: string,
    config: HttpRequestConfig<P> & { showOriginResponse: true }
  ): Promise<HTTPProtocol<T> | null>
  async post<P = null, T = null>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse?: false }
  ): Promise<T | null>
  async post<P = null, T = null>(
    url: string,
    config: HttpRequestConfig<P> & { showOriginResponse?: boolean } = {}
  ): Promise<T | HTTPProtocol<T> | null> {
    if (config && config.showOriginResponse === true) {
      return this.request<P, T>(url, {
        ...(config as HttpRequestConfig<P>),
        method: "POST",
        showOriginResponse: true,
      })
    }
    return this.request<P, T>(url, {
      ...(config as HttpRequestConfig<P>),
      method: "POST",
      showOriginResponse: false,
    })
  }

  async safeGet<P = null, T = null>(
    url: string,
    config: HttpRequestConfig<P> & { showOriginResponse: true }
  ): Promise<[HttpError | null, HTTPProtocol<T> | null]>
  async safeGet<P = null, T = null>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse?: false }
  ): Promise<[HttpError | null, T | null]>
  async safeGet<P = null, T = null>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse?: boolean }
  ): Promise<[HttpError | null, T | HTTPProtocol<T> | null]> {
    try {
      if (config && config.showOriginResponse === true) {
        const data = await this.request<P, T>(url, {
          ...(config as HttpRequestConfig<P>),
          method: "GET",
          showOriginResponse: true,
        })
        return [null, data]
      } else {
        const data = await this.request<P, T>(url, {
          ...(config as HttpRequestConfig<P>),
          method: "GET",
          showOriginResponse: false,
        })
        return [null, data]
      }
    } catch (error) {
      if (error instanceof HttpError) {
        return [error, null]
      }
      return [new HttpError(0, -1, error instanceof Error ? error.message : "未知错误"), null]
    }
  }

  async safePost<P = null, T = null>(
    url: string,
    config: HttpRequestConfig<P> & { showOriginResponse: true }
  ): Promise<[HttpError | null, HTTPProtocol<T> | null]>
  async safePost<P = null, T = null>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse?: false }
  ): Promise<[HttpError | null, T | null]>
  async safePost<P = null, T = null>(
    url: string,
    config?: HttpRequestConfig<P> & { showOriginResponse?: boolean }
  ): Promise<[HttpError | null, T | HTTPProtocol<T> | null]> {
    try {
      if (config && config.showOriginResponse === true) {
        const data = await this.request<P, T>(url, {
          ...(config as HttpRequestConfig<P>),
          method: "POST",
          showOriginResponse: true,
        })
        return [null, data]
      } else {
        const data = await this.request<P, T>(url, {
          ...(config as HttpRequestConfig<P>),
          method: "POST",
          showOriginResponse: false,
        })
        return [null, data]
      }
    } catch (error) {
      if (error instanceof HttpError) {
        return [error, null]
      }
      return [new HttpError(0, -1, error instanceof Error ? error.message : "未知错误"), null]
    }
  }
}

export { Http }
export const http = new Http()
